import type { BlockType } from '@unifyapps/defs/types/block';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { JavaScriptVariable } from '@unifyapps/defs/types/javascriptVariable';
import _keyBy from 'lodash/keyBy';
import _omit from 'lodash/omit';
import { type RefObject, useContext, useEffect } from 'react';
import type { BuilderInterfaceStoreSelectors } from '../builder/selectors';
import {
  ensureUniqueBlockDisplayNameWithPage,
  ensureUniquePageVariableNameWithPage,
} from '../builder/utils/ensureUniqueBlockDisplayNames';
import { getUniqueDisplayNameContext } from '../builder/utils/getUniqueDisplayNameContext';
import { InterfaceAppStoreContext } from '../context';
import type { RunnerInterfaceStoreSelectors } from '../runner/selectors';
import { getActiveInterfacePageFromStore } from '../runner/state';
import type { InterfaceStoreRefType } from '../types';

export function useInterfaceStoreImperativeHandle({
  interfaceStoreRef,
  store,
  getDataSourceRecords,
  getAppDataSourceRecords,
}: {
  interfaceStoreRef: RefObject<InterfaceStoreRefType | undefined>;
  store: RunnerInterfaceStoreSelectors | BuilderInterfaceStoreSelectors;
  getDataSourceRecords?: () => Record<string, DataSourceEntity | undefined>;
  getAppDataSourceRecords?: () => Record<string, DataSourceEntity | undefined>;
}) {
  // Get app store context value (may be null if not available, e.g., when used in app store provider)
  const appStoreContext = useContext(InterfaceAppStoreContext);

  useEffect(() => {
    if (interfaceStoreRef) {
      // Type assertion is safe here because InterfaceAppStoreContext provides either
      // RunnerInterfaceStoreSelectors or BuilderInterfaceStoreSelectors when available
      const appStore = appStoreContext as
        | (RunnerInterfaceStoreSelectors | BuilderInterfaceStoreSelectors)
        | null;

      // Create getter for app store state - used by getUniqueDisplayNameContext
      const getAppInterfaceStoreState = appStore ? () => appStore.getState() : undefined;

      interfaceStoreRef.current = {
        getPageScopeKey: () => store.getState().pageScopeKey,
        updateBlocks: ({
          added,
          changed,
          deleted = [],
        }: {
          added: BlockType[];
          deleted: BlockType[];
          changed: BlockType[];
        }) => {
          const interfaceStoreState = store.getState();
          const interfacePage = getActiveInterfacePageFromStore(interfaceStoreState);

          if (interfacePage) {
            const context = getUniqueDisplayNameContext({
              interfacePage,
              dataSources: getDataSourceRecords?.() ?? {},
              appDataSources: getAppDataSourceRecords?.() ?? {},
              getAppInterfaceStoreState,
            });

            // Ensure unique displayNames for added and changed blocks
            // Accumulate processed blocks to prevent duplicates within the batch
            const blocksToUpdate: BlockType[] = [];
            const accumulatedBlocks = { ...context.blocks };

            for (const block of [...added, ...changed]) {
              const blockWithUniqueDisplayName = ensureUniqueBlockDisplayNameWithPage({
                block,
                interfacePage,
                dataSources: context.dataSources,
                appPageVariables: context.pageVariables,
                appPageFunctions: context.pageFunctions,
                appBlocks: accumulatedBlocks,
                appFolders: context.folders,
                appWalkthroughSteps: context.walkthroughSteps,
              });

              blocksToUpdate.push(blockWithUniqueDisplayName);
              // Add to accumulated blocks so next iteration sees this block's displayName
              accumulatedBlocks[blockWithUniqueDisplayName.id] = blockWithUniqueDisplayName;
            }

            const keyByBlocks = _keyBy(blocksToUpdate, 'id');

            const newBlocks = _omit(
              {
                ...interfacePage.properties.blocks,
                ...keyByBlocks,
              },
              deleted.map((block) => block.id),
            );

            store.getState().actions.updateInterfacePageAction({
              interfacePage: {
                ...interfacePage,
                properties: {
                  ...interfacePage.properties,
                  blocks: newBlocks,
                },
              },
            });
          }
        },
        updatePageVariables: ({
          added,
          changed,
          deleted = [],
        }: {
          added: JavaScriptVariable[];
          deleted: JavaScriptVariable[];
          changed: JavaScriptVariable[];
        }) => {
          const interfaceStoreState = store.getState();
          const interfacePage = getActiveInterfacePageFromStore(interfaceStoreState);

          if (interfacePage) {
            const context = getUniqueDisplayNameContext({
              interfacePage,
              dataSources: getDataSourceRecords?.() ?? {},
              appDataSources: getAppDataSourceRecords?.() ?? {},
              getAppInterfaceStoreState,
            });

            // Ensure unique names for added and changed variables
            // Accumulate processed variables to prevent duplicates within the batch
            const varsToUpdate: JavaScriptVariable[] = [];
            const accumulatedPageVariables = { ...context.pageVariables };

            for (const variable of [...added, ...changed]) {
              const variableWithUniqueName = ensureUniquePageVariableNameWithPage({
                pageVariable: variable,
                interfacePage,
                dataSources: context.dataSources,
                appPageVariables: accumulatedPageVariables,
                appPageFunctions: context.pageFunctions,
                appBlocks: context.blocks,
                appFolders: context.folders,
                appWalkthroughSteps: context.walkthroughSteps,
              });

              varsToUpdate.push(variableWithUniqueName);
              // Add to accumulated variables so next iteration sees this variable's name
              accumulatedPageVariables[variableWithUniqueName.id] = variableWithUniqueName;
            }

            const keyByVariables = _keyBy(varsToUpdate, 'id');

            const newVars = _omit(
              {
                ...(interfacePage.properties.pageVariables ?? {}),
                ...keyByVariables,
              },
              deleted.map((v) => v.id),
            );

            store.getState().actions.updateInterfacePageAction({
              interfacePage: {
                ...interfacePage,
                properties: {
                  ...interfacePage.properties,
                  pageVariables: newVars,
                },
              },
            });
          }
        },
      };
    }
  }, [interfaceStoreRef, store, appStoreContext, getDataSourceRecords, getAppDataSourceRecords]);
}
